import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../api_config";
import {
  FaCreditCard,
  FaShieldAlt,
  FaCrown,
  FaStar,
  FaHeart,
  FaCheckCircle,
  FaGift,
  FaRocket
} from "react-icons/fa";
import UserContext from "../../Authcontext";

import PhonePePayment from "./PhonePePayment";
import "./payment.css";

function Payment({ userId }) {
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionAlert, setShowSubscriptionAlert] = useState(false);
  const [activeTab, setActiveTab] = useState("plans"); // "plans" or "current"
  const { token, isAuthenticated, userId: contextUserId } = useContext(UserContext);
  const [activePlanName, setActivePlanName] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // On mount, fetch current membership if authenticated
    const fetchMembership = async () => {
      try {
        // Always show plans by default while fetching
        setShowSubscriptionAlert(true);
        // Determine effective user id
        const effectiveUserId = userId || contextUserId || (function () {
          if (!token) return null;
          try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            return payload.userId || payload.id || payload.sub || null;
          } catch (e) {
            return null;
          }
        })();

        if (!effectiveUserId || !token) {
          setLoading(false);
          return;
        }

        const res = await axios.get(
          `${API_BASE_URL}/api/user-payment-status/${effectiveUserId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Normalize response
        if (res.data && res.data.success && res.data.paymentDetails) {
          const pd = res.data.paymentDetails;
          setPaymentDetails([
            {
              pay_membership: pd.membership,
              pay_time: pd.paymentTime,
              expiry_date: pd.expiryDate,
              status: res.data.status,
            },
          ]);
          setActivePlanName(pd.membership || null);
        } else if (Array.isArray(res.data)) {
          // Fallback to /api/payment-details shape if server returns array
          setPaymentDetails(res.data);
          if (res.data.length) setActivePlanName(res.data[0].pay_membership);
        }
      } catch (err) {
        console.error("Failed to fetch membership:", err?.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMembership();
  }, [API_BASE_URL, token, userId, contextUserId]);

  const getCurrentMembership = () => {
    if (!paymentDetails.length) return null;

    // Find the most recent payment
    const sortedPayments = [...paymentDetails].sort((a, b) =>
      new Date(b.pay_time) - new Date(a.pay_time)
    );

    return sortedPayments[0];
  };

  if (loading) {
    return (
      <div className="premium-payment-container">
        <div className="payment-hero">
          <div className="hero-content">
            <div className="hero-icon">
              <FaCrown />
            </div>
            <h1 className="hero-title">
              Loading Your
              <span className="gradient-text"> Premium Experience</span>
            </h1>
          </div>
        </div>

        <div className="loading-container">
          <div className="spinner-border" style={{ color: "#667eea" }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h3 style={{ marginTop: "20px", color: "#333" }}>Preparing your membership options...</h3>
          <p style={{ color: "#666", marginTop: "10px" }}>This should only take a moment</p>

          <button
            onClick={() => {
              setLoading(false);
              setShowSubscriptionAlert(true);
            }}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              background: "#667eea",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer"
            }}
          >
            Continue to Plans
          </button>
        </div>
      </div>
    );
  }

  const currentMembership = getCurrentMembership();

  return (
    <div className="premium-payment-container">
      {/* Hero Section */}
      <div className="payment-hero">
        <div className="hero-content">
          <div className="hero-icon">
            <FaCrown />
          </div>
          <h1 className="hero-title">
            Find Your Perfect Match with
            <span className="gradient-text"> Premium Membership</span>
          </h1>
          <p className="hero-subtitle">
            Join thousands of successful couples who found love through our premium features
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <FaHeart className="stat-icon" />
              <span>10,000+ Happy Couples</span>
            </div>
            <div className="stat-item">
              <FaStar className="stat-icon" />
              <span>4.8/5 Success Rate</span>
            </div>
            <div className="stat-item">
              <FaShieldAlt className="stat-icon" />
              <span>100% Verified Profiles</span>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Tab Navigation */}
      <div className="premium-tabs">
        <div className="tab-container">
          <button
            className={`premium-tab ${activeTab === "plans" ? "active" : ""}`}
            onClick={() => setActiveTab("plans")}
          >
            <FaRocket className="tab-icon" />
            <span>Choose Your Plan</span>
          </button>
          <button
            className={`premium-tab ${activeTab === "current" ? "active" : ""}`}
            onClick={() => setActiveTab("current")}
          >
            <FaCheckCircle className="tab-icon" />
            <span>My Membership</span>
          </button>
        </div>
      </div>

      {/* Current Plan Tab - Enhanced */}
      {activeTab === "current" && currentMembership && (
        <div className="current-membership-enhanced">
          <div className="membership-card">
            <div className="membership-header">
              <div className="membership-badge">
                <FaCrown />
                <span>Active Membership</span>
              </div>
              <h2>{currentMembership.pay_membership} Plan</h2>
            </div>

            <div className="membership-details">
              <div className="detail-row">
                <span className="detail-label">Activated On</span>
                <span className="detail-value">
                  {new Date(currentMembership.pay_time).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              {currentMembership.expiry_date && (
                <div className="detail-row">
                  <span className="detail-label">Valid Until</span>
                  <span className="detail-value">
                    {new Date(currentMembership.expiry_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>

            <div className="membership-actions">
              <button
                onClick={() => setActiveTab("plans")}
                className="upgrade-button"
              >
                <FaGift />
                Upgrade to Premium
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "current" && !currentMembership && (
        <div className="no-membership-container">
          <div className="no-membership-card">
            <div className="empty-state-icon">
              <FaCreditCard />
            </div>
            <h2>Start Your Premium Journey</h2>
            <p>Unlock exclusive features and connect with verified profiles to find your perfect match.</p>
            <div className="benefits-preview">
              <div className="benefit-item">
                <FaCheckCircle />
                <span>35 Profile Views</span>
              </div>
              <div className="benefit-item">
                <FaCheckCircle />
                <span>Advanced Matching Algorithm</span>
              </div>
              <div className="benefit-item">
                <FaCheckCircle />
                <span>Priority Customer Support</span>
              </div>
            </div>
            <button
              onClick={() => setActiveTab("plans")}
              className="get-started-button"
            >
              <FaRocket />
              Get Started Now
            </button>
          </div>
        </div>
      )}

      {/* Subscription Plans Tab - Enhanced */}
      {activeTab === "plans" && (
        <div className="plans-section">

          <PhonePePayment
            userId={userId}
            activePlanName={activePlanName}
            onPaymentSuccess={() => {
              setShowSubscriptionAlert(false);
              setActiveTab("current");
              window.location.reload();
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Payment;